import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLowStockProducts, getLowStockParts } from '../data/inventory';
import { useInventory } from '../context/InventoryContext';
import './LowStockInventory.css';

type LowStockItem =
  | { type: 'product'; id: string; name: string; availability: number }
  | { type: 'part'; productName: string; availability: number; source: 'tim-e' | 'bim-e' };

const LowStockInventory: React.FC = () => {
  const navigate = useNavigate();
  const { products: inventoryProducts, units, timEParts, bimEParts } = useInventory();

  const lowStockProducts = useMemo(() => getLowStockProducts(inventoryProducts, units), [inventoryProducts, units]);
  const lowStockParts = useMemo(
    () => getLowStockParts(timEParts, bimEParts, inventoryProducts, units),
    [timEParts, bimEParts, inventoryProducts, units]
  );

  const combined: LowStockItem[] = useMemo(() => {
    const list: LowStockItem[] = lowStockProducts.map(({ product, availability }) => ({
      type: 'product' as const,
      id: product.id,
      name: product.name,
      availability,
    }));
    const seen = new Set(list.map((item) => (item.type === 'product' ? item.id : '')));
    for (const { productName, availability, source, productId } of lowStockParts) {
      if (productId) {
        if (seen.has(productId)) continue;
        seen.add(productId);
        list.push({ type: 'product', id: productId, name: productName, availability });
      } else {
        list.push({ type: 'part', productName, availability, source });
      }
    }
    list.sort((a, b) => a.availability - b.availability);
    return list;
  }, [lowStockProducts, lowStockParts]);

  const botProductIds = useMemo(() => {
    const timE = inventoryProducts.find((p) => p.name === 'TIM-E Bot');
    const bimE = inventoryProducts.find((p) => p.name === 'BIM-E bot');
    return { 'tim-e': timE?.id, 'bim-e': bimE?.id };
  }, [inventoryProducts]);

  const handleClick = (item: LowStockItem) => {
    if (item.type === 'product') {
      navigate(`/inventory/product/${item.id}`);
      return;
    }
    /** Parts sit outside the catalog: their table lives on the bot page they build. */
    const botId = botProductIds[item.source];
    if (botId) navigate(`/inventory/product/${botId}?part=${encodeURIComponent(item.productName)}`);
    else navigate('/inventory');
  };

  return (
    <div className="dashboard-card low-stock-card">
      <h3 className="card-title" style={{ cursor: 'pointer' }} onClick={() => navigate('/inventory')}>
        Low stock
      </h3>
      <div className="card-content">
        {combined.length === 0 ? (
          <p className="low-stock-empty">No low-stock items. Set a buffer on a product or TIM-E/BIM-E part to see it here when stock falls below that number.</p>
        ) : (
          <ul className="low-stock-list">
            {combined.map((item, index) => (
              <li
                key={item.type === 'product' ? item.id : `${item.source}-${item.productName}-${index}`}
                className="low-stock-row low-stock-yellow"
                onClick={() => handleClick(item)}
              >
                <span className="low-stock-name">
                  {item.type === 'product' ? item.name : `${item.productName} (${item.source === 'tim-e' ? 'TIM-E' : 'BIM-E bot'})`}
                </span>
                <span className="low-stock-count" aria-label={`${item.availability} units available`}>
                  {item.availability} units available
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default LowStockInventory;
